// resources/js/app.ts
import { createLogger } from '@/core/logger';
import { createScanner } from '@/core/scanner';

import '@/widgets'; // ensure widgets are registered

export function boot() {
    if (!window.easti.config) {
        console.error('[easti] missing config');
        return;
    }
    const cfg = window.easti.config;

    window.easti.log = createLogger(cfg.env);
    window.easti.log.info('booting', cfg);

    const scanner = createScanner(cfg);

    const rescan = (root: Document | HTMLElement) => {
        window.easti.log.info('[easti] Reconciling widgets in scope', root);
        scanner.reconcile(root);
    };

    // Initial scan
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => rescan(document), {
            once: true,
        });
    } else {
        rescan(document);
    }

    const mountSel = `[${cfg.mountAttr}]`;
    const propsAttr = cfg.propsAttr;

    function touchesMount(m: MutationRecord): boolean {
        if (m.type === 'attributes') {
            const t = m.target as Element;
            if (!t) return false;
            // mount attr changed on any element
            if (m.attributeName === cfg.mountAttr) return true;
            // props changed on an actual mount element
            if (m.attributeName === propsAttr && t.hasAttribute(cfg.mountAttr)) return true;
            return false;
        }
        if (m.type === 'childList') {
            // If any added/removed subtree contains a mount, rescan
            const nodes = [...m.addedNodes, ...m.removedNodes];
            for (const n of nodes) {
                if (n.nodeType !== 1) continue;
                const el = n as Element;
                if (el.hasAttribute(cfg.mountAttr) || el.querySelector(mountSel)) return true;
            }
        }
        return false;
    }

    let rafId = 0;
    let debounceId = 0 as unknown as number;
    const DEBOUNCE_MS = 80; // tune 50–150ms as needed

    function scheduleRescan() {
        // Collapse many mutations in same frame
        if (!rafId) {
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                // trailing debounce to catch immediate follow-up DOM churn
                clearTimeout(debounceId);
                debounceId = window.setTimeout(() => rescan(document), DEBOUNCE_MS);
            });
        }
    }

    const mo = new MutationObserver((mutations) => {
        // Fast prefilter: ignore noise
        if (!mutations.some(touchesMount)) return;
        window.easti.log.info('[easti] DOM mutation detected (batched)');
        scheduleRescan();
    });

    // Narrow observation to scopeRoot; limit attributes to the two you care about
    mo.observe(document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [cfg.mountAttr, propsAttr],
    });
}

boot();
